import * as React from 'react';
import Moment from "moment";
import { IAuthorizationRelationData } from './AuthorizationDomainModel';
import { IIdNamePair } from '../../services/ResourceRelationsService';
import AuthorizationLevelAutocomplete from './AuthorizationLevelAutocomplete';
import lang from '../Relations/RelationsLang';
import { IConfig } from './Models';
import { ErrorMessagesList } from '../Relations/ErrorMessagesIconTooltip';
import { DatesFieldFormRow, LabelFormRow } from '../Relations/FormsControls';
import { Button, Dialog, DialogActions, DialogContent } from '@material-ui/core';
import DialogTitleWithClose from '../../components/DialogTitleWithClose';
import { useTranslation } from 'react-i18next';

export interface IEditingIAuthorizationRelationData extends IAuthorizationRelationData {
	copy: boolean,
}

export interface IEditRowControlResult {
	from: Moment.Moment;
	to: Moment.Moment;
	level: IIdNamePair;
};

interface IProps {
	allPermissionLevels: IIdNamePair[];
	resourceName: string;
	row: IEditingIAuthorizationRelationData;

	config: IConfig,
	onSaved: (result: IEditRowControlResult) => void,
	onCancel: () => void,
}

export default function AuthorizationRelationEditRowDialog(props: IProps) {

	const { t } = useTranslation();

	const [dateError, setDateError] = React.useState("");
	const [selectedFrom, setSelectedFrom] = React.useState<Moment.Moment>(null);
	const [selectedTo, setSelectedTo] = React.useState<Moment.Moment>(null);
	const [permissionLevelError, setPermissionLevelError] = React.useState("");
	const [selectedPermissionLevel, setSelectedPermissionLevel] = React.useState<IIdNamePair>(null);

	const fields = props.config.fields;

	React.useEffect(() => {
		function parse(datetime: Moment.Moment | string): Moment.Moment {
			if (!datetime)
				return null;

			var item: Moment.Moment
			if (typeof datetime === "string")
				item = Moment(datetime);
			else
				item = datetime;

			return Moment({
				year: item.year(),
				month: item.month(),
				day: item.date()
			});
		}

		let lvl: IIdNamePair = null;
		if (props.row?.level)
			lvl = { ...props.row.level }

		setDateError("");
		setSelectedFrom(parse(props.row?.from));
		setSelectedTo(parse(props.row?.to));
		setPermissionLevelError("");
		setSelectedPermissionLevel(lvl);

	}, [props.row]);

	function isValid(): boolean {
		let from = selectedFrom;
		if (!from) {
			from = Moment({ h: 0, m: 0, s: 0, ms: 0 });
		}

		let to = selectedTo;

		let result = true;

		let dateError = "";
		let permissionLevelError = "";

		if (fields.level.required) {
			if (selectedPermissionLevel == null) {
				permissionLevelError = lang.permissionLevelRequiredError();
				result = false;
			}
		}

		if (to) {
			if (from > to) {
				dateError = "control.relation.fromToErrorMessage";
				result = false;
			}
		}

		if (fields.dateTo.required) {
			if (!to) {
				let label = lang.getDefaultByPrefix(fields.dateTo, "label", "to");
				dateError = lang.fieldIsRequiredError(label);
				result = false;
			}
		}

		setDateError(dateError);
		setPermissionLevelError(permissionLevelError);

		return result;
	}

	function getValues(): IEditRowControlResult {
		let from = selectedFrom;
		if (!from) {
			from = Moment({ h: 0, m: 0, s: 0, ms: 0 });
		}

		return {
			from: from,
			to: selectedTo,
			level: selectedPermissionLevel,
		}
	}

	function handleSaveRow() {
		if (isValid())
			props.onSaved(getValues());
	}

	if (!props.row)
		return null;

	return <Dialog
		open={true}
		onClose={props.onCancel}
		disableBackdropClick={true}
	>
		<DialogTitleWithClose
			onClose={props.onCancel}
		>
			{!props.row.copy && <span>{t("control.relation.editing")}</span>}
			{props.row.copy && <span>{t("control.relation.copying")}</span>}
			{' '}
			{props.row.resource.label}
		</DialogTitleWithClose>
		<DialogContent>
			<div className="edit-input-row">
				<ErrorMessagesList errorMessages={props.row.errorMessages} />

				{permissionLevelError && <p className="text-danger">{permissionLevelError}</p>}
				{!fields.level.hidden && <dl className="form-field">
					<dt>
						<LabelFormRow
							config={fields.level}
							name="level"
						/>
					</dt>
					<dd>
						<RenderPermissionLevelInput
							allPermissionLevels={props.allPermissionLevels}
							currentLevel={props.row.level}
							onSelect={(d) => setSelectedPermissionLevel(d)}
							selectedPermissionLevel={selectedPermissionLevel}
						/>
					</dd>
				</dl>}

				<DatesFieldFormRow
					editRow={!!props.row.id}
					errorText={dateError}
					dateFromConfig={fields.dateFrom}
					dateToConfig={fields.dateTo}
					from={selectedFrom}
					to={selectedTo}
					onChange={(from, to) => {
						setSelectedFrom(from);
						setSelectedTo(to);
					}}
				/>
			</div>
		</DialogContent>
		<DialogActions>
			<Button onClick={props.onCancel} color="default">
				{t("common.action.cancel")}
			</Button>
			<Button onClick={handleSaveRow} color="primary" variant="contained">
				{t("common.action.save")}
			</Button>
		</DialogActions>
	</Dialog>
}

interface IRenderPermissionLevelInputProps {
	allPermissionLevels: IIdNamePair[],

	selectedPermissionLevel: IIdNamePair,
	onSelect: (selectedPermissionLevel: IIdNamePair) => void,

	currentLevel: IIdNamePair,
}

function RenderPermissionLevelInput(props: IRenderPermissionLevelInputProps) {

	const [permissions, setPermissions] = React.useState<IIdNamePair[]>([]);

	React.useEffect(() => {

		let newPermissions = props.allPermissionLevels
		if (props.currentLevel && (!props.currentLevel.id || props.currentLevel.id == -1)) {
			newPermissions = [];
			newPermissions.push({ id: -1, name: props.currentLevel.name });
			newPermissions = newPermissions.concat(props.allPermissionLevels);
		}

		setPermissions(newPermissions);
	}, [props.allPermissionLevels, props.currentLevel]);

	return <div>
		<div className="permission-role-single-control">
			<AuthorizationLevelAutocomplete
				value={props.selectedPermissionLevel}
				onSelect={props.onSelect}
				options={permissions}
			/>
		</div>
	</div>
}